import { useState } from "react";
import { Pencil, Plus, QrCode } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { request } from "@/lib/api";

const emptyOrgForm = {
  client_id: "",
  name: "",
  subdomain: "",
  allowed_email_domains: "",
  notification_from_name: "",
  logo_url: "",
  brand_color: "",
  sponsorship_interval_days: 366,
  sponsored_relationships: ["self"],
  is_active: true,
};

// Who a company can choose to pay for. "self" is deliberately absent: an
// employee's own check is the benefit the arrangement exists for, so it is
// always covered and is not offered as something to switch off.
const COVERABLE = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "dependent", label: "Dependent" },
];

export default function OrganisationsSection({ token, clients, onChanged }) {
  const [orgForm, setOrgForm] = useState(emptyOrgForm);
  const [editingClientId, setEditingClientId] = useState("");
  const [orgError, setOrgError] = useState("");
  const [inviteQr, setInviteQr] = useState(null);

  function startAddOrg() {
    setEditingClientId("");
    setOrgForm(emptyOrgForm);
    setOrgError("");
  }

  function startEditOrg(client) {
    setEditingClientId(client.client_id);
    setOrgForm({
      client_id: client.client_id,
      name: client.name,
      subdomain: client.subdomain || "",
      allowed_email_domains: (client.allowed_email_domains || []).join(", "),
      notification_from_name: client.notification_from_name,
      logo_url: client.logo_url || "",
      brand_color: client.brand_color || "",
      sponsorship_interval_days: client.sponsorship_interval_days ?? 366,
      sponsored_relationships: client.sponsored_relationships || ["self"],
      is_active: client.is_active,
    });
    setOrgError("");
  }

  const updateOrgForm = (key, value) => setOrgForm((current) => ({ ...current, [key]: value }));

  async function openInviteQr(client) {
    try {
      setInviteQr(await request(`/admin/clients/${client.client_id}/invite-qr`, {}, token));
    } catch (qrError) {
      setOrgError(qrError.message);
    }
  }

  async function saveOrg(event) {
    event.preventDefault();
    setOrgError("");
    const payload = {
      name: orgForm.name,
      subdomain: orgForm.subdomain || null,
      // The authoritative tenant boundary — see clients.allowed_email_domains
      // in the ORM. Typos here decide who can sign in.
      allowed_email_domains: orgForm.allowed_email_domains.split(",").map((domain) => domain.trim()).filter(Boolean),
      notification_from_name: orgForm.notification_from_name,
      logo_url: orgForm.logo_url || null,
      brand_color: orgForm.brand_color || null,
      sponsorship_interval_days: Number(orgForm.sponsorship_interval_days) || 366,
      sponsored_relationships: orgForm.sponsored_relationships,
      is_active: orgForm.is_active,
    };
    try {
      if (editingClientId) {
        await request(`/admin/clients/${editingClientId}`, { method: "PUT", body: JSON.stringify(payload) }, token);
      } else {
        await request("/admin/clients", { method: "POST", body: JSON.stringify({ ...payload, client_id: orgForm.client_id }) }, token);
      }
      await onChanged?.();
      startAddOrg();
    } catch (saveError) {
      setOrgError(saveError.message);
    }
  }

  return (
    <section className="ops-panel" data-testid="ops-organisations">
      <header className="ops-panel__head">
        <div>
          <h1>Organisations</h1>
          <p>{clients.length} tenant{clients.length === 1 ? "" : "s"} on the platform</p>
        </div>
        <Button type="button" variant="outline" onClick={startAddOrg} data-testid="admin-add-org-button">
          <Plus size={16} />Add organisation
        </Button>
      </header>

      <div className="ops-card-grid" data-testid="admin-org-list">
        {clients.map((client) => (
          <article className="ops-org-card" key={client.client_id} data-testid={`admin-org-row-${client.client_id}`}>
            <div className="ops-org-card__head">
              <strong>{client.name}</strong>
              <span className={`ops-pill ${client.is_active ? "is-good" : "is-muted"}`}>{client.is_active ? "Active" : "Inactive"}</span>
            </div>
            <dl className="ops-org-card__facts">
              <div><dt>Subdomain</dt><dd>{client.subdomain ? `${client.subdomain}.ivoryhealth.in` : "—"}</dd></div>
              <div><dt>Email domains</dt><dd>{(client.allowed_email_domains || []).join(", ") || "—"}</dd></div>
              <div><dt>Sponsored again after</dt><dd>{client.sponsorship_interval_days ?? 366} days</dd></div>
              <div><dt>Company pays for</dt><dd>{(client.sponsored_relationships || ["self"]).map((item) => item === "self" ? "Employee" : item[0].toUpperCase() + item.slice(1)).join(", ")}</dd></div>
            </dl>
            <div className="ops-org-card__actions">
              <Button type="button" variant="outline" size="sm" onClick={() => startEditOrg(client)} data-testid={`admin-edit-org-${client.client_id}-button`}>
                <Pencil size={14} />Edit
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => openInviteQr(client)} data-testid="admin-invite-qr-button">
                <QrCode size={14} />Invite QR
              </Button>
            </div>
          </article>
        ))}
      </div>

      <form className="ops-card page-form" onSubmit={saveOrg} data-testid="admin-org-form">
        <p className="eyebrow">{editingClientId ? `Editing ${editingClientId}` : "New organisation"}</p>
        {!editingClientId && (
          <>
            <label htmlFor="org-client-id">Client ID (slug)</label>
            <input id="org-client-id" value={orgForm.client_id} onChange={(event) => updateOrgForm("client_id", event.target.value)} placeholder="e.g. acme-corp" required data-testid="admin-org-client-id-input" />
          </>
        )}
        <label htmlFor="org-name">Display name</label>
        <input id="org-name" value={orgForm.name} onChange={(event) => updateOrgForm("name", event.target.value)} required data-testid="admin-org-name-input" />
        <label htmlFor="org-subdomain">Subdomain</label>
        <input id="org-subdomain" value={orgForm.subdomain} onChange={(event) => updateOrgForm("subdomain", event.target.value)} data-testid="admin-org-subdomain-input" />
        <label htmlFor="org-domains">Allowed email domains (comma-separated)</label>
        <input id="org-domains" value={orgForm.allowed_email_domains} onChange={(event) => updateOrgForm("allowed_email_domains", event.target.value)} required data-testid="admin-org-domains-input" />
        <small className="employee-muted">This decides who can sign in to this tenant. Nothing else does.</small>
        <label htmlFor="org-notification-name">Notification sender name</label>
        <input id="org-notification-name" value={orgForm.notification_from_name} onChange={(event) => updateOrgForm("notification_from_name", event.target.value)} required data-testid="admin-org-notification-name-input" />
        <label htmlFor="org-interval">Sponsor the next check-up after</label>
        <div className="admin-color-field">
          <input id="org-interval" inputMode="numeric" value={orgForm.sponsorship_interval_days}
            onChange={(event) => updateOrgForm("sponsorship_interval_days", event.target.value.replace(/\D/g, ""))}
            data-testid="admin-org-interval-input" />
          <span className="employee-muted">days since the last one</span>
        </div>
        <small className="employee-muted">
          366 means the day after a full year. Employees can always book sooner — they pay for it themselves.
        </small>

        <label>Company also pays for</label>
        <div className="admin-cover-options" data-testid="admin-org-cover-options">
          {COVERABLE.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={orgForm.sponsored_relationships.includes(option.value)}
                onChange={(event) => updateOrgForm(
                  "sponsored_relationships",
                  event.target.checked
                    ? [...orgForm.sponsored_relationships, option.value]
                    : orgForm.sponsored_relationships.filter((item) => item !== option.value),
                )}
                data-testid={`admin-org-cover-${option.value}`}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <small className="employee-muted">
          The employee is always covered. Anyone ticked here books at no charge and waits the same interval
          between sponsored check-ups; anyone not ticked can still be booked, and pays.
        </small>

        <label htmlFor="org-logo">Logo URL</label>
        <input id="org-logo" value={orgForm.logo_url} onChange={(event) => updateOrgForm("logo_url", event.target.value)} data-testid="admin-org-logo-input" />
        <label htmlFor="org-color">Brand colour</label>
        <div className="admin-color-field">
          <input id="org-color" type="color" value={/^#[0-9A-Fa-f]{6}$/.test(orgForm.brand_color) ? orgForm.brand_color : "#166534"} onChange={(event) => updateOrgForm("brand_color", event.target.value)} data-testid="admin-org-color-input" />
          <input value={orgForm.brand_color} onChange={(event) => updateOrgForm("brand_color", event.target.value)} placeholder="#166534" data-testid="admin-org-color-text" />
        </div>
        <label className="ops-checkbox" htmlFor="org-active">
          <input id="org-active" type="checkbox" checked={orgForm.is_active} onChange={(event) => updateOrgForm("is_active", event.target.checked)} data-testid="admin-org-active-input" />
          <span>Active</span>
        </label>
        {orgError && <p className="form-error" role="alert" data-testid="admin-org-error">{orgError}</p>}
        <Button type="submit" data-testid="admin-org-save-button">{editingClientId ? "Save changes" : "Create organisation"}</Button>
      </form>

      <Dialog open={Boolean(inviteQr)} onOpenChange={(open) => !open && setInviteQr(null)}>
        <DialogContent data-testid="admin-invite-qr-dialog">
          <DialogHeader><DialogTitle>Invite {inviteQr?.client_name}</DialogTitle></DialogHeader>
          {inviteQr && (
            <>
              <img src={inviteQr.image} alt={`Invite QR for ${inviteQr.client_name}`} data-testid="admin-invite-qr-image" />
              <p>{inviteQr.url}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
